import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { kycApi } from '../../services/api';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';

// Количество строк на странице
const PAGE_SIZE = 10;

const KycDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Запрос данных о KYC документах
  const { data, isLoading, error, refetch } = useQuery(
    ['kycDocuments', page, searchTerm, filters],
    () => {
      const queryParams: any = { page: page + 1, limit: PAGE_SIZE };
      
      if (searchTerm) {
        queryParams.search = searchTerm;
      }
      
      if (filters.type) {
        queryParams.type = filters.type;
      }
      
      if (filters.status) {
        queryParams.status = filters.status;
      }
      
      return kycApi.getDocuments(page + 1, PAGE_SIZE, queryParams);
    },
    {
      keepPreviousData: true,
    }
  );
  
  // Обработчик изменения страницы
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  
  // Обработчик изменения поискового запроса
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  // Обработчик изменения фильтров
  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  
  // Обработчик сброса фильтров
  const handleResetFilters = () => {
    setFilters({
      type: '',
      status: '',
    });
  };
  
  // Обработчик нажатия на строку таблицы
  const handleRowClick = (params: any) => {
    navigate(`/kyc/${params.row.id}`);
  };
  
  // Функция для отображения типа документа
  const getDocumentTypeText = (type: string) => {
    switch (type) {
      case 'passport':
        return 'Паспорт';
      case 'driverLicense':
        return 'Водительские права';
      case 'idCard':
        return 'Удостоверение личности';
      case 'proofOfAddress':
        return 'Подтверждение адреса';
      default:
        return type;
    }
  };
  
  // Определение колонок таблицы
  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'userId', 
      headerName: 'ID пользователя', 
      width: 130,
      renderCell: (params) => (
        <Button 
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/users/${params.value}`);
          }}
        >
          {params.value}
        </Button>
      ),
    },
    { 
      field: 'type', 
      headerName: 'Тип документа', 
      width: 180,
      valueFormatter: (params) => getDocumentTypeText(params.value as string),
    },
    { 
      field: 'status', 
      headerName: 'Статус', 
      width: 150,
      renderCell: (params) => {
        const status = params.value as string;
        
        if (status === 'approved') {
          return <Chip icon={<VerifiedUserIcon />} label="Подтвержден" color="success" size="small" />;
        } else if (status === 'pending') {
          return <Chip icon={<PendingIcon />} label="На проверке" color="warning" size="small" />;
        } else {
          return <Chip icon={<CancelIcon />} label="Отклонен" color="error" size="small" />;
        }
      }
    },
    { 
      field: 'createdAt', 
      headerName: 'Дата загрузки', 
      width: 180,
      valueFormatter: (params) => 
        format(new Date(params.value as string), 'dd.MM.yyyy HH:mm', { locale: ru }),
    },
    {
      field: 'fileUrl',
      headerName: 'Документ',
      width: 120,
      renderCell: (params) => (
        <Button 
          variant="outlined" 
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            window.open(params.value as string, '_blank');
          }}
        >
          Просмотр
        </Button>
      ),
    },
  ];
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error">
        Ошибка при загрузке данных. Пожалуйста, попробуйте позже.
      </Alert>
    );
  }
  
  const rows = data?.data.data || [];
  const totalRows = data?.data.total || 0;
  
  return (
    <Grid container spacing={3}>
      {/* Заголовок страницы */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            KYC Документы
          </Typography>
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ mr: 1 }}
            >
              {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Обновить
            </Button>
          </Box>
        </Box>
      </Grid>
      
      {/* Поиск */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            id="search"
            placeholder="Поиск по ID документа или ID пользователя"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          {/* Фильтры */}
          {showFilters && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="type-filter-label">Тип документа</InputLabel>
                    <Select
                      labelId="type-filter-label"
                      id="type-filter"
                      value={filters.type}
                      label="Тип документа"
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                    >
                      <MenuItem value="">Все</MenuItem>
                      <MenuItem value="passport">Паспорт</MenuItem>
                      <MenuItem value="driverLicense">Водительские права</MenuItem>
                      <MenuItem value="idCard">Удостоверение личности</MenuItem>
                      <MenuItem value="proofOfAddress">Подтверждение адреса</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="status-filter-label">Статус</InputLabel>
                    <Select
                      labelId="status-filter-label"
                      id="status-filter"
                      value={filters.status}
                      label="Статус"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">Все</MenuItem>
                      <MenuItem value="pending">На проверке</MenuItem>
                      <MenuItem value="approved">Подтвержден</MenuItem>
                      <MenuItem value="rejected">Отклонен</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleResetFilters}
                    >
                      Сбросить фильтры
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Grid>
      
      {/* Таблица KYC документов */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, height: 600 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pagination
            paginationMode="server"
            rowCount={totalRows}
            pageSizeOptions={[PAGE_SIZE]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: PAGE_SIZE, page },
              },
            }}
            onPaginationModelChange={(model) => handlePageChange(model.page)}
            onRowClick={handleRowClick}
            disableRowSelectionOnClick
            loading={isLoading}
            localeText={{
              noRowsLabel: 'Нет данных',
              footerRowSelected: (count: number) => `Выбрано ${count} строк`,
            }}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default KycDocumentsPage; 